import type { TaskDraft, TaskItem } from "./types";

export const DAY_MS = 86_400_000;
export type TimelineDragMode = "move" | "start" | "end";
export interface TaskDateRange { start: string; end: string }
export interface TaskHistoryPatches {
  before: Partial<TaskDraft>;
  after: Partial<TaskDraft>;
}

export function parseDate(value: string): Date {
  return new Date(`${value}T12:00:00`);
}

export function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function addDays(value: string, amount: number): string {
  const date = parseDate(value);
  date.setDate(date.getDate() + amount);
  return dateKey(date);
}

export function diffDays(from: string, to: string): number {
  return Math.round((parseDate(to).getTime() - parseDate(from).getTime()) / DAY_MS);
}

export function mondayOf(value: Date): string {
  const date = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return dateKey(date);
}

export function taskRange(task: TaskItem): TaskDateRange | null {
  const rawStart = task.scheduled ?? task.timelineStart;
  const rawEnd = task.due ?? task.timelineEnd ?? rawStart;
  if (!rawStart && !rawEnd) return null;
  const start = rawStart ?? rawEnd!;
  const end = rawEnd ?? rawStart!;
  return start <= end ? { start, end } : { start: end, end: start };
}

export function compareTaskTimelineOrder(a: TaskItem, b: TaskItem): number {
  return a.sortOrder - b.sortOrder
    || a.createdAt.localeCompare(b.createdAt)
    || a.id.localeCompare(b.id);
}

export function adjustTaskRange(range: TaskDateRange, mode: TimelineDragMode, amount: number): TaskDateRange {
  if (mode === "move") {
    return { start: addDays(range.start, amount), end: addDays(range.end, amount) };
  }
  if (mode === "start") {
    const start = addDays(range.start, amount);
    return { start: start > range.end ? range.end : start, end: range.end };
  }
  const end = addDays(range.end, amount);
  return { start: range.start, end: end < range.start ? range.start : end };
}

export function taskRangePatch(task: TaskItem, range: TaskDateRange) {
  return { scheduled: range.start, due: range.end };
}

export function moveTaskRange(task: TaskItem, amount: number) {
  const fallback = taskRange(task);
  if (!fallback) return {};
  const start = task.scheduled ?? task.timelineStart ?? task.due ?? fallback.start;
  const end = task.due ?? task.timelineEnd ?? task.scheduled ?? fallback.end;
  return { scheduled: addDays(start, amount), due: addDays(end, amount) };
}

export function applyTaskPatch(task: TaskItem, patch: Partial<TaskDraft>): TaskItem {
  return {
    ...task,
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
    ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
    ...(patch.projectId !== undefined ? { projectId: patch.projectId } : {}),
    ...(patch.goalId !== undefined ? { goalId: patch.goalId } : {}),
    ...(patch.categoryIds !== undefined ? { categoryIds: patch.categoryIds } : {}),
    ...(patch.due !== undefined ? { due: patch.due } : {}),
    ...(patch.scheduled !== undefined ? { scheduled: patch.scheduled } : {}),
    ...(patch.timelineStart !== undefined ? { timelineStart: patch.timelineStart } : {}),
    ...(patch.timelineEnd !== undefined ? { timelineEnd: patch.timelineEnd } : {}),
    ...(patch.status !== undefined ? { status: patch.status } : {}),
    ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
  };
}

function taskPatchForChangedFields(task: TaskItem, patch: Partial<TaskDraft>): Partial<TaskDraft> {
  return {
    ...(patch.title !== undefined ? { title: task.title } : {}),
    ...(patch.notes !== undefined ? { notes: task.notes } : {}),
    ...(patch.tags !== undefined ? { tags: task.tags } : {}),
    ...(patch.projectId !== undefined ? { projectId: task.projectId } : {}),
    ...(patch.goalId !== undefined ? { goalId: task.goalId } : {}),
    ...(patch.categoryIds !== undefined ? { categoryIds: [...task.categoryIds] } : {}),
    ...(patch.due !== undefined ? { due: task.due } : {}),
    ...(patch.scheduled !== undefined ? { scheduled: task.scheduled } : {}),
    ...(patch.timelineStart !== undefined ? { timelineStart: task.timelineStart } : {}),
    ...(patch.timelineEnd !== undefined ? { timelineEnd: task.timelineEnd } : {}),
    ...(patch.status !== undefined ? { status: task.status } : {}),
    ...(patch.priority !== undefined ? { priority: task.priority } : {}),
  };
}

export function createTaskHistoryPatches(task: TaskItem, patch: Partial<TaskDraft>): TaskHistoryPatches | null {
  const before = taskPatchForChangedFields(task, patch);
  const after = taskPatchForChangedFields(applyTaskPatch(task, patch), patch);
  return JSON.stringify(before) === JSON.stringify(after) ? null : { before, after };
}
